import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Constants from '../../submodules/logictry_config/constants';
import WindowSize from '../../services/WindowSize';
import UserCache from '../../services/cache/UserCache';
import LogicBaseSearchCache from '../../services/cache/LogicBaseSearchCache';
import ContentRailCard from '../ContentRailCard';
import CollectionRailCard from '../CollectionRailCard';
import VerticalCenterContent from '../../styledhtml/VerticalCenterContent';
import {
  DiscoverFeedSection,
  SectionHeader,
  SectionTitle,
  SectionHint,
  RailTrack,
} from '../../styledhtml/LogicBaseHomeLayout';
import { SEARCH_CONTENT_CARD_WIDTH } from '../../styledhtml/LogicBaseCard';
import { SearchIcon } from '../../styledhtml/Icon';
import Popup from '../Popup';
import Scrollable from '../Scrollable';
import LogicBaseExpertCard from '../LogicBaseExpertCard';
import LoadingIndicator from '../LoadingIndicator';
import LogictryComponent from '../../submodules/logictry_config/LogictryComponent';
const Search = styled.div`
  width: 100%;
  max-width: 38rem;
  position: relative;
  overflow: hidden;
  margin: 0.5rem auto;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  cursor: text;
  border: ${Constants.LightBorder};
  background: transparent;
  ${(p) => p.$dock && `
    margin: 0;
    max-width: none;
    min-height: 44px;
    border-radius: 999px;
    border: 1px solid ${Constants.HairlineBorderRgba};
    background: ${Constants.SurfaceColor};
    box-shadow: ${Constants.isDarkMode ? '0 4px 16px rgba(255, 255, 255, 0.04)' : '0 4px 16px rgba(0, 0, 0, 0.08)'};
    padding: 0 0.85rem 0 1rem;
    gap: 0.5rem;
  `}
  ${(p) => p.$icon && `
    width: 44px;
    height: 44px;
    min-height: 44px;
    max-width: 44px;
    margin: 0;
    padding: 0;
    justify-content: center;
    flex-shrink: 0;
    overflow: visible;
    cursor: pointer;
    ${p.$dock ? '' : `
      border: 0;
      background: transparent;
      box-shadow: none;
      border-radius: 12px;
    `}
  `}
  ${Constants.MediaMobile} {
    border: ${(p) => (p.$dock ? `1px solid ${Constants.HairlineBorderRgba}` : 'unset')};
    background: ${(p) => (p.$dock ? Constants.SurfaceColor : 'transparent')};
  }
  ${(p) => !p.$icon && `
    > div:first-child {
      width: 100%;
      position: relative;
      background: ${p.$dock ? 'transparent' : Constants.SurfaceColor};
      color: #999;
      ${p.$dock ? `
        padding: 0.65rem 0;
        font-size: ${Constants.SemiSmallFontSize};
        color: ${Constants.MediumTextGray};
      ` : ''}
    }
  `}
`;

const PopupShell = styled.div`
  ${Constants.PopupHeight}
  width: calc(${Constants.ListPopupWidth} - 2rem);
  max-width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`;

const ScrollRegion = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HeaderBlock = styled.div`
  flex-shrink: 0;
  padding: 1rem 1.25rem 1rem;
  background: ${Constants.SurfaceColor};
`;

const SearchField = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 1rem;
  background: ${Constants.StaticGray};
  border: 1px solid ${Constants.LightBorderColor};
  border-radius: 12px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus-within {
    border-color: ${Constants.PrimaryColor};
    box-shadow: 0 0 0 3px color-mix(in srgb, ${Constants.PrimaryColor} 22%, transparent);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-size: ${Constants.NormalFontSize};
  color: ${Constants.DarkTextColor};
  outline: none;
  line-height: 1.4;
  &::placeholder {
    color: ${Constants.MediumTextGray};
  }
`;

const ResultsBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1rem 1.25rem 2rem;
  min-height: min(40vh, 320px);
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem 1.25rem 1rem;
  color: ${Constants.MediumTextGray};
  font-size: ${Constants.SemiSmallFontSize};
  line-height: 1.5;
`;

const EmptyTitle = styled.div`
  font-size: ${Constants.NormalFontSize};
  font-weight: 600;
  color: ${Constants.DarkTextColor};
  margin-bottom: 0.375rem;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 2.5rem 1rem;
`;

/** Same layout as home `NewCollectionsMobileList` — vertical stack on narrow viewports. */
const SearchCollectionsMobileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  min-width: 0;
  padding-top: 0.35rem;
  box-sizing: border-box;
`;

export default class LogicBaseSearchBar extends LogictryComponent {
  static propTypes = {
    variant: PropTypes.oneOf(['default', 'dock']),
  };

  state = {
    popupOpen: false,
    initialInputFocus: false,
  }
  constructor() {
    super();
    this.innerHeight = window.innerHeight;
  }
  componentDidMount() {
    WindowSize.onStateUpdate(this);
    LogicBaseSearchCache.onStateUpdate(this);
    UserCache.onStateUpdate(this);
  }
  componentWillUnmount() {
    WindowSize.offStateUpdate(this);
    LogicBaseSearchCache.offStateUpdate(this);
    UserCache.offStateUpdate(this);
    clearTimeout(this.__searchTimeout);
  }
  componentDidUpdate(_, prevState) {
    const { popupOpen, search } = this.state;
    if (!popupOpen || search === prevState.search) return;
    const resetScroll = () => {
      if (this.scrollRef) this.scrollRef.scrollTop = 0;
      [this.searchPeopleRailScrollEl, this.searchContentRailScrollEl, this.searchCollectionRailScrollEl].forEach((el) => {
        if (el) el.scrollLeft = 0;
      });
    };
    resetScroll();
    requestAnimationFrame(resetScroll);
  }
  onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (this.__input) this.__input.blur();
      this.runSearch();
    } else {
      clearTimeout(this.__searchTimeout);
      this.__searchTimeout = setTimeout(() => {
        this.runSearch();
      }, 500);
    }
  }
  runSearch = () => {
    const search = this.__input.value.trim();
    this.setState({ search });
  }
  render() {
    const { mobile } = WindowSize;
    const { search, popupOpen } = this.state;
    const { variant = 'default' } = this.props;
    const isDock = variant === 'dock';
    const iconOnly = mobile && !isDock;

    let resultsBody = null;

    if (!popupOpen) {
      resultsBody = null;
    } else if (!search) {
      resultsBody = (
        <EmptyState>
          <EmptyTitle>Search Logictry</EmptyTitle>
          Find content, collections, and people with one search.
        </EmptyState>
      );
    } else {
      const unifiedRaw = LogicBaseSearchCache.query({ q: search, content_limit: 24, collection_limit: 24 });
      const usersSearched = UserCache.getUsers({ page: 1, search });
      const allSettled = unifiedRaw !== null && usersSearched !== null;

      if (!allSettled) {
        resultsBody = (
          <LoadingWrap><LoadingIndicator /></LoadingWrap>
        );
      } else {
        const contentHits = (unifiedRaw.content || []).filter((r) => r && !r.error);
        const collectionsRows = (unifiedRaw.collections || []).filter((c) => c && !c.error);
        const usersRows = (usersSearched || []).filter((u) => u && !u.error);
        const hasContent = contentHits.length > 0;
        const hasCollections = collectionsRows.length > 0;
        const hasPeople = usersRows.length > 0;

        if (!hasContent && !hasCollections && !hasPeople) {
          resultsBody = (
            <EmptyState>
              <EmptyTitle>No results</EmptyTitle>
              Try different keywords or check spelling.
            </EmptyState>
          );
        } else {
          resultsBody = (
            <>
              <DiscoverFeedSection>
                <SectionHeader>
                  <SectionTitle>People</SectionTitle>
                </SectionHeader>
                {hasPeople ? (
                  <Scrollable
                    horizontal
                    showScrollButtons
                    adjustForScrollbar
                    hideScrollbar
                    scrollRef={(el) => { this.searchPeopleRailScrollEl = el; }}
                    style={{ padding: '0 0 0.5rem' }}
                  >
                    <RailTrack>
                      {usersRows.map((user) => (
                        <LogicBaseExpertCard key={user._id} lbUser={user} layout="rail" />
                      ))}
                    </RailTrack>
                  </Scrollable>
                ) : (
                  <SectionHint style={{ margin: '0 0 0.5rem' }}>No matching people.</SectionHint>
                )}
              </DiscoverFeedSection>

              <DiscoverFeedSection>
                <SectionHeader>
                  <SectionTitle>Content</SectionTitle>
                </SectionHeader>
                {hasContent ? (
                  <Scrollable
                    horizontal
                    showScrollButtons
                    adjustForScrollbar
                    hideScrollbar
                    scrollRef={(el) => { this.searchContentRailScrollEl = el; }}
                    style={{ padding: '0 0 0.5rem' }}
                  >
                    <RailTrack>
                      {contentHits.map((hit) => {
                        const rowKey = String(hit.contentId || hit.tree?._id || '');
                        const tree = hit.tree;
                        if (!tree) {
                          return (
                            <VerticalCenterContent key={rowKey} style={{ aspectRatio: '16 / 9', width: SEARCH_CONTENT_CARD_WIDTH }}>
                              <LoadingIndicator />
                            </VerticalCenterContent>
                          );
                        }
                        const descText = tree.description?.text
                          ?? tree.description
                          ?? hit.description
                          ?? '';
                        const cardTree = typeof descText === 'string' && descText
                          ? { ...tree, description: { text: descText } }
                          : tree;
                        return (
                          <ContentRailCard
                            key={rowKey}
                            tree={cardTree}
                            collectionId={hit.collectionId || undefined}
                            captionTitle
                            compact
                          />
                        );
                      })}
                    </RailTrack>
                  </Scrollable>
                ) : (
                  <SectionHint style={{ margin: '0 0 0.5rem' }}>No matching content.</SectionHint>
                )}
              </DiscoverFeedSection>

              <DiscoverFeedSection>
                <SectionHeader>
                  <SectionTitle>Collections</SectionTitle>
                </SectionHeader>
                {hasCollections ? (
                  mobile ? (
                    <SearchCollectionsMobileList>
                      {collectionsRows.map((collection) => (
                        <CollectionRailCard
                          fullWidth
                          key={collection._id}
                          collection={collection}
                        />
                      ))}
                    </SearchCollectionsMobileList>
                  ) : (
                    <Scrollable
                      horizontal
                      showScrollButtons
                      adjustForScrollbar
                      hideScrollbar
                      scrollRef={(el) => { this.searchCollectionRailScrollEl = el; }}
                      style={{ padding: '0 0 0.5rem' }}
                    >
                      <RailTrack>
                        {collectionsRows.map((collection) => (
                          <CollectionRailCard
                            key={collection._id}
                            collection={collection}
                          />
                        ))}
                      </RailTrack>
                    </Scrollable>
                  )
                ) : (
                  <SectionHint style={{ margin: '0 0 0.5rem' }}>No matching collections.</SectionHint>
                )}
              </DiscoverFeedSection>
            </>
          );
        }
      }
    }

    return <>
      <Search
        $dock={isDock}
        $icon={iconOnly}
        role={iconOnly ? 'button' : undefined}
        tabIndex={iconOnly ? 0 : undefined}
        aria-label="Search"
        onClick={() => this.setState({ initialInputFocus: false, popupOpen: true })}
        onKeyDown={iconOnly ? (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.setState({ initialInputFocus: false, popupOpen: true });
          }
        } : undefined}
      >
        {!iconOnly && <div
          style={isDock ? undefined : { padding: '10px 0 10px 1rem' }}
        >
          Search
        </div>}
        <SearchIcon
          style={{
            width: isDock ? 18 : 20,
            height: isDock ? 18 : 20,
            opacity: isDock ? 0.45 : (iconOnly ? 1 : 0.3),
            margin: (!iconOnly && !isDock) ? '10px 16px' : 0,
            flexShrink: 0,
            filter: Constants.isDarkMode ? 'invert(1)' : undefined,
          }}
          aria-hidden="true"
        />
      </Search>
      {popupOpen && <Popup
        showCloseButton
        allowBackgroundClose
        onClose={() => {
          clearTimeout(this.__searchTimeout);
          this.setState({
            popupOpen: false,
            search: '',
          });
        }}
      >
        <PopupShell
          style={{ height: Constants.isIphone && `calc(${this.innerHeight}px - 6rem - var(--logictry-popup-safe-top, 0px) - var(--logictry-popup-safe-bottom, 0px))` }}
        >
          <HeaderBlock>
            <SearchField>
              <SearchIcon
                style={{
                  width: 20,
                  height: 20,
                  opacity: 0.42,
                  flexShrink: 0,
                  filter: Constants.isDarkMode ? 'invert(1)' : undefined,
                }}
                aria-hidden="true"
              />
              <SearchInput
                ref={(e) => {
                  const { initialInputFocus } = this.state;
                  if (!e) return;
                  this.__input = e;
                  if (initialInputFocus) return;
                  this.state.initialInputFocus = true;
                  e.focus();
                }}
                enterKeyHint="search"
                onKeyDown={this.onKeyDown}
                placeholder="Search content, collections, people…"
                aria-label="Search Logictry"
              />
            </SearchField>
          </HeaderBlock>
          <ScrollRegion>
            <Scrollable
              scrollRef={(e) => { this.scrollRef = e; }}
              vertical
              style={{ height: '100%', maxHeight: '100%', width: '100%' }}
              adjustForScrollbar
              onScroll={() => { if (this.__input) this.__input.blur(); }}
            >
              <ResultsBody>
                {resultsBody}
              </ResultsBody>
            </Scrollable>
          </ScrollRegion>
        </PopupShell>
      </Popup>}
    </>
  }
}
